import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { MarkdownContent } from '@backstage/core-components';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { ReadmePreviewFieldUiOptions } from './schema';

/**
 * Backstage Scaffolder custom field extension — use in a template.yaml with
 * ui:field: ReadmePreviewField.
 *
 * Renders a markdown editor on the left with a live markdown preview on the
 * right. Returns the raw markdown string. Configurable via ui:options
 * (inputLabel, previewLabel, rows, placeholder).
 *
 * @param props FieldExtensionComponentProps injected by the Scaffolder
 * @returns ReadmePreviewField React Element
 */
export const ReadmePreviewField = (
  props: FieldExtensionComponentProps<string, ReadmePreviewFieldUiOptions>,
) => {
  const {
    onChange,
    formData,
    uiSchema,
    schema: { title, description },
    required,
    disabled,
    readonly,
  } = props;

  const options = uiSchema['ui:options'];
  const inputLabel = options?.inputLabel ?? 'Readme';
  const previewLabel = options?.previewLabel ?? 'Preview';
  const rows = options?.rows ?? 10;
  const placeholder = options?.placeholder;

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const backgroundColor = isDark ? '#0D0D0D' : '#F4F4F4';
  const previewBackgroundColor = isDark ? '#1B1B1B' : '#E5E5E5';

  const value = formData ?? '';

  return (
    <Box sx={{ backgroundColor, padding: '1rem', borderRadius: '0.25rem' }}>
      {title && (
        <Typography
          variant="h6"
          sx={{
            marginBottom: 1,
            marginTop: 0,
            color: theme.palette.text.primary,
          }}
        >
          {title}
          {required ? ' *' : ''}
        </Typography>
      )}
      {description && (
        <Typography
          variant="body2"
          sx={{
            marginBottom: 1,
            marginTop: 0,
            color: theme.palette.text.primary,
          }}
        >
          {description}
        </Typography>
      )}

      {(title || description) && (
        <Divider sx={{ marginBottom: 2, marginTop: 0.5 }} />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          alignItems: 'stretch',
        }}
      >
        <FormControl fullWidth variant="standard" sx={{ flex: 1 }}>
          <InputLabel
            sx={{ variant: 'subtitle2', marginTop: 2 }}
            shrink
            htmlFor="readme-preview-input"
          >
            {inputLabel}
          </InputLabel>
          <TextField
            id="readme-preview-input"
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            multiline
            rows={rows}
            fullWidth
            variant="standard"
            InputProps={{ readOnly: readonly }}
            sx={{ marginTop: 5 }}
          />
        </FormControl>

        <Box
          sx={{
            flex: 1,
            backgroundColor: previewBackgroundColor,
            borderRadius: '0.25rem',
            padding: 2,
            overflow: 'auto',
            maxHeight: `${rows * 1.75 + 4}rem`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.text.secondary, marginBottom: 1 }}
          >
            {previewLabel}
          </Typography>
          {value ? <MarkdownContent content={value} /> : null}
        </Box>
      </Box>
    </Box>
  );
};
